import React, { useState, useEffect } from 'react';
import API from '../../services/api';

const DocumentReportsPage = () => {
    const [reports, setReports] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // History Modal States
    const [selectedBikeHistory, setSelectedBikeHistory] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const limit = 15;

    useEffect(() => {
        fetchReports(currentPage, statusFilter, searchTerm);
    }, [currentPage, statusFilter]);

    const fetchReports = async (page, status, search) => {
        try {
            let url = `/documents/reports?page=${page}&limit=${limit}`;
            if (status) url += `&status=${status}`;
            if (search) url += `&search=${search}`;

            const res = await API.get(url);
            if (res.data.success) {
                setReports(res.data.data);
                setTotalPages(res.data.totalPages);
            }
        } catch (err) {
            console.error("Error fetching reports:", err);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchReports(1, statusFilter, searchTerm);
    };

    const fetchBikeHistory = async (stockId) => {
        setLoadingHistory(true);
        setShowModal(true);
        try {
            const res = await API.get(`/documents/history/${stockId}`);
            if (res.data.success) {
                setSelectedBikeHistory(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching history:", err);
        } finally {
            setLoadingHistory(false);
        }
    };

    return (
        <div style={containerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#0f172a' }}>📊 Detailed Document Reports & Life-cycle History</h3>
                <div>
                    <button onClick={() => { setStatusFilter(''); setCurrentPage(1); }} style={tabBtn(statusFilter === '')}>All</button>
                    <button onClick={() => { setStatusFilter('Pending'); setCurrentPage(1); }} style={tabBtn(statusFilter === 'Pending')}>Pending</button>
                    <button onClick={() => { setStatusFilter('Available'); setCurrentPage(1); }} style={tabBtn(statusFilter === 'Available')}>Available</button>
                    <button onClick={() => { setStatusFilter('Issued'); setCurrentPage(1); }} style={tabBtn(statusFilter === 'Issued')}>Issued</button>
                </div>
            </div>

            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <input 
                    type="text" 
                    placeholder="Search by Engine No, Chassis No, Model or Lot ID..." 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={inputStyle}
                />
                <button type="submit" style={btnPrimary}>Search</button>
            </form>

            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                <table style={tableStyle}>
                    <thead>
                        <tr style={{ background: '#f8fafc', color: '#475569' }}>
                            <th style={thTd}>Lot ID</th>
                            <th style={thTd}>Model</th>
                            <th style={thTd}>Engine No</th>
                            <th style={thTd}>Chassis No</th>
                            <th style={thTd}>Status</th>
                            <th style={thTd}>Doc Type</th>
                            <th style={thTd}>Location</th>
                            <th style={thTd}>Current Info</th>
                            <th style={thTd}>History</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports.length > 0 ? reports.map(r => (
                            <tr key={r.inventory_stock_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={thTd}><b>{r.lot_id || 'N/A'}</b></td>
                                <td style={thTd}>{r.model_name}</td>
                                <td style={thTd}>{r.engine_number}</td>
                                <td style={thTd}>{r.chassis_number}</td>
                                <td style={thTd}><span style={badgeStyle(r.doc_status)}>{r.doc_status}</span></td>
                                <td style={thTd}>{r.document_type || '-'}</td>
                                <td style={thTd}>{r.physical_location || '-'}</td>
                                <td style={thTd}>
                                    {r.doc_status === 'Issued' 
                                        ? `Issued to: ${r.issued_to}` 
                                        : (r.received_by ? `Rec by: ${r.received_by}` : '-')}
                                </td>
                                <td style={{ ...thTd, textAlign: 'center' }}>
                                    <button onClick={() => fetchBikeHistory(r.inventory_stock_id)} style={btnHistory}>
                                        View History
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="9" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>No records found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                <button 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    style={{ ...paginationBtn, opacity: currentPage === 1 ? 0.5 : 1 }}
                >
                    ◀ Previous
                </button>
                <span style={{ color: '#334155' }}>Page <b>{currentPage}</b> of <b>{totalPages || 1}</b></span>
                <button 
                    disabled={currentPage >= totalPages} 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    style={{ ...paginationBtn, opacity: currentPage >= totalPages ? 0.5 : 1 }}
                >
                    Next ▶
                </button>
            </div>

            {/* History Modal */}
            {showModal && (
                <div style={modalOverlay}>
                    <div style={modalContent}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '15px' }}>
                            <h4 style={{ margin: 0 }}>📜 Bike Document Life-cycle History</h4>
                            <button onClick={() => setShowModal(false)} style={closeBtn}>✕</button>
                        </div>
                        {loadingHistory ? (
                            <p style={{ textAlign: 'center', padding: '20px' }}>Loading history...</p>
                        ) : (
                            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                {selectedBikeHistory && selectedBikeHistory.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                                                <th style={{ padding: '8px' }}>Action</th>
                                                <th style={{ padding: '8px' }}>Status</th>
                                                <th style={{ padding: '8px' }}>Doc Type</th>
                                                <th style={{ padding: '8px' }}>Received/Issued By</th>
                                                <th style={{ padding: '8px' }}>Date</th>
                                                <th style={{ padding: '8px' }}>Remarks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedBikeHistory.map((h, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px' }}><b>{h.action_type}</b></td>
                                                    <td style={{ padding: '8px' }}><span style={badgeStyle(h.doc_status)}>{h.doc_status}</span></td>
                                                    <td style={{ padding: '8px' }}>{h.document_type || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{h.received_by || h.issued_to || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{h.created_at ? h.created_at.replace('T', ' ').substring(0, 16) : '-'}</td>
                                                    <td style={{ padding: '8px' }}>{h.remarks || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p style={{ textAlign: 'center', padding: '20px' }}>No previous history logs found for this bike.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const containerStyle = { fontFamily: 'Arial, sans-serif' };
const inputStyle = { flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' };
const btnPrimary = { background: '#0284c7', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const btnHistory = { background: '#0f172a', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' };
const thTd = { padding: '12px 15px' };
const tabBtn = (isActive) => ({ padding: '8px 14px', marginRight: '5px', background: isActive ? '#0284c7' : '#e2e8f0', color: isActive ? '#fff' : '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' });
const paginationBtn = { background: '#334155', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const badgeStyle = (status) => ({
    padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold',
    background: status === 'Available' ? '#dcfce7' : (status === 'Issued' ? '#fef3c7' : '#fee2e2'),
    color: status === 'Available' ? '#166534' : (status === 'Issued' ? '#92400e' : '#991b1b')
});
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContent = { background: '#fff', padding: '20px', borderRadius: '8px', width: '700px', maxWidth: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' };
const closeBtn = { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', fontWeight: 'bold' };

export default DocumentReportsPage;